import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { extractBrandColorFromDomain } from "~/helpers/brand-color-extractor";
import { DEFAULT_BRAND_COLOR, normalizeHexColor } from "~/helpers/brand-color";
import { apiClient } from "~/lib/api-client";
import { cn } from "~/lib/utils";
import { toast } from "sonner";

interface EditableBrandColorSwatchProps {
  domain: string;
  color: string;
  teamDomain: string;
  size?: "sm" | "md";
  onSaved?: (domain: string, color: string) => void;
}

export default function EditableBrandColorSwatch({
  domain,
  color,
  teamDomain,
  size = "md",
  onSaved,
}: EditableBrandColorSwatchProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(color);
  const [isSaving, setIsSaving] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(color);
    }
  }, [open, color]);

  const displayColor = normalizeHexColor(draft) ?? DEFAULT_BRAND_COLOR;
  const swatchColor = normalizeHexColor(color) ?? DEFAULT_BRAND_COLOR;

  async function handleSave() {
    const normalized = normalizeHexColor(draft);
    if (!normalized) {
      toast.error("Enter a valid hex color");
      return;
    }

    setIsSaving(true);
    try {
      if (domain === teamDomain) {
        await apiClient.put("/teams", { brand_color: normalized });
      } else {
        await apiClient.patch(`/competitors/${encodeURIComponent(domain)}`, {
          brand_color: normalized,
        });
      }
      onSaved?.(domain, normalized);
      setOpen(false);
    } catch {
      toast.error("Failed to save color");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleExtractFromLogo() {
    setIsExtracting(true);
    try {
      const result = await extractBrandColorFromDomain(domain);
      if (result?.hex) {
        setDraft(result.hex);
      }
    } finally {
      setIsExtracting(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Change chart color"
          aria-label={`Change chart color for ${domain}`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            "shrink-0 cursor-pointer rounded-sm ring-2 ring-transparent transition-shadow hover:ring-orange-400/60 focus-visible:outline-none focus-visible:ring-orange-500",
            size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"
          )}
          style={{ backgroundColor: swatchColor }}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-2"
        align="start"
        side="top"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Chart color
        </p>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={displayColor}
            onChange={(e) => setDraft(e.target.value)}
            className="h-8 w-8 cursor-pointer rounded border border-gray-200 p-0.5"
          />
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-8 w-[5.5rem] font-mono text-xs"
          />
          <Button
            type="button"
            size="sm"
            className="h-8 px-2 text-xs"
            disabled={isSaving}
            onClick={handleSave}
          >
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
          </Button>
        </div>
        <button
          type="button"
          className="mt-1.5 text-[10px] text-muted-foreground hover:text-foreground"
          disabled={isExtracting}
          onClick={handleExtractFromLogo}
        >
          {isExtracting ? "Reading logo…" : "Use color from logo"}
        </button>
      </PopoverContent>
    </Popover>
  );
}
